import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, animate, useInView } from "framer-motion";
import {
  Shield, QrCode, Siren, Users, Car, MessageCircle, ShieldAlert, Search, Package, Dog, Baby,
  ArrowRight, ArrowUpRight, Check, Sparkles, MapPin, Bell, Lock, Sun, Moon, Star,
  Phone, Radio, Zap, Award, HeartHandshake, Play, ChevronDown, Quote, Download, Route,
  Fingerprint, Ambulance, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LangContext";
import LangToggle from "@/components/LangToggle";

// -------- Reusable animation helpers --------
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

function StoreLink({ href, kicker, label, coming, testId }) {
  const disabled = !href;
  const cls = "rounded-2xl border border-border/70 bg-card px-5 py-3 flex items-center gap-3 hover:-translate-y-0.5 transition-transform relative";
  const inner = (
    <>
      <Download className="w-4 h-4" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{kicker}</div>
        <div className="font-semibold text-sm">{label}</div>
      </div>
      {disabled && (
        <span className="ml-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] font-semibold">
          {coming}
        </span>
      )}
    </>
  );
  if (disabled) {
    return <div data-testid={testId} className={`${cls} opacity-80 cursor-not-allowed`}>{inner}</div>;
  }
  return (
    <a data-testid={testId} href={href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
  );
}

function CountUp({ to, suffix = "", duration = 2 }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, { duration, ease: "easeOut", onUpdate: (v) => setDisplay(Math.round(v)) });
    return controls.stop;
  }, [inView, to, mv, duration]);
  return (
    <span ref={ref}>
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

// -------- Hero Collage (rich animated image mosaic) --------
function HeroCollage({ t }) {
  const orbitIcons = [QrCode, Users, MapPin, Bell];
  return (
    <div className="relative aspect-[4/5] max-w-md mx-auto" data-testid="hero-collage">
      {/* Radar rings */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none" aria-hidden>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute w-72 h-72 rounded-full border-2 border-[hsl(var(--primary))]/25"
            animate={{ scale: [1, 1.7, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 4, delay: i * 1.3, repeat: Infinity, ease: "easeOut" }}
          />
        ))}
      </div>

      {/* Top-left tilted image card */}
      <motion.div
        initial={{ opacity: 0, x: -40, rotate: -14 }}
        animate={{ opacity: 1, x: 0, rotate: -8 }}
        transition={{ duration: 0.9, delay: 0.35, type: "spring", stiffness: 90 }}
        whileHover={{ rotate: -3, scale: 1.04, zIndex: 40 }}
        className="absolute -top-4 -left-6 w-40 aspect-[3/4] rounded-2xl overflow-hidden border-4 border-background shadow-2xl z-30"
      >
        <motion.img
          src="https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
          alt=""
          className="w-full h-full object-cover"
          animate={{ scale: [1.05, 1.15, 1.05] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute bottom-2 left-2 right-2 glass rounded-xl px-2 py-1.5"
        >
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--accent))] font-bold">{t("hero_alert_label")}</div>
          <div className="text-[10px] font-semibold truncate text-foreground">{t("hero_alert_title")}</div>
        </motion.div>
      </motion.div>

      {/* Main image with Ken Burns zoom */}
      <div className="relative aspect-[4/5] rounded-3xl overflow-hidden card-shadow z-10">
        <motion.img
          src="https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: [1.15, 1.3, 1.15], x: [0, -8, 0], y: [0, 5, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))]/25 via-transparent to-[hsl(var(--destructive))]/20 mix-blend-overlay" />

        {/* Orbiting module icons around SOS */}
        <div className="absolute inset-0 grid place-items-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
            className="relative w-60 h-60"
          >
            {orbitIcons.map((Ico, i) => {
              const angle = (i * 90 * Math.PI) / 180;
              const r = 115;
              return (
                <div
                  key={i}
                  style={{ left: `calc(50% + ${Math.cos(angle) * r}px)`, top: `calc(50% + ${Math.sin(angle) * r}px)` }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-xl bg-background/95 backdrop-blur grid place-items-center shadow-xl border border-border/60"
                >
                  <motion.div animate={{ rotate: -360 }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }}>
                    <Ico className="w-4 h-4 text-[hsl(var(--primary))]" />
                  </motion.div>
                </div>
              );
            })}
          </motion.div>

          {/* Center SOS pulse */}
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
            className="absolute w-24 h-24 rounded-full bg-[hsl(var(--destructive))] grid place-items-center shadow-2xl z-10"
          >
            <motion.span
              className="absolute inset-0 rounded-full bg-[hsl(var(--destructive))]"
              animate={{ scale: [1, 1.7, 1], opacity: [0.55, 0, 0.55] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.span
              className="absolute inset-0 rounded-full bg-[hsl(var(--destructive))]"
              animate={{ scale: [1, 2.1, 1], opacity: [0.35, 0, 0.35] }}
              transition={{ duration: 2.6, repeat: Infinity, delay: 0.3 }}
            />
            <Siren className="w-10 h-10 text-white relative" />
          </motion.div>
        </div>
      </div>

      {/* Bottom-right tilted image card */}
      <motion.div
        initial={{ opacity: 0, x: 40, rotate: 14 }}
        animate={{ opacity: 1, x: 0, rotate: 8 }}
        transition={{ duration: 0.9, delay: 0.55, type: "spring", stiffness: 90 }}
        whileHover={{ rotate: 3, scale: 1.04, zIndex: 40 }}
        className="absolute -bottom-6 -right-6 w-44 aspect-[3/4] rounded-2xl overflow-hidden border-4 border-background shadow-2xl z-30"
      >
        <motion.img
          src="https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
          alt=""
          className="w-full h-full object-cover"
          animate={{ scale: [1.05, 1.18, 1.05] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <motion.div
          animate={{ y: [0, 4, 0] }}
          transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
          className="absolute bottom-2 left-2 right-2 glass rounded-xl px-2 py-1.5"
        >
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--primary))] font-bold">{t("hero_scan_label")}</div>
          <div className="text-[10px] font-semibold truncate text-foreground">{t("hero_scan_title")}</div>
        </motion.div>
      </motion.div>

      {/* Floating stat pill — Response time */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="absolute top-1/3 -right-4 lg:-right-10 z-40"
      >
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="glass rounded-2xl px-3 py-2 flex items-center gap-2.5 shadow-xl border border-border/40"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--secondary))] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--secondary))]" />
          </span>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">Response</div>
            <div className="text-sm font-display font-black tracking-tighter">3.2s <span className="text-[hsl(var(--secondary))] text-[9px] font-bold">AVG</span></div>
          </div>
        </motion.div>
      </motion.div>

      {/* Floating stat pill — Families protected */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="absolute bottom-20 -left-4 lg:-left-12 z-40"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
          className="glass rounded-2xl px-3 py-2 flex items-center gap-2.5 shadow-xl border border-border/40"
        >
          <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/15 grid place-items-center">
            <Users className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">Families safe</div>
            <div className="text-sm font-display font-black tracking-tighter">12,847</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Floating stat pill — QR scans (top-right small) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.3, type: "spring" }}
        className="absolute -top-6 right-8 z-40"
      >
        <motion.div
          animate={{ rotate: [0, 3, -3, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="glass rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-lg border border-border/40"
        >
          <QrCode className="w-3 h-3 text-[hsl(var(--accent))]" />
          <span className="text-[10px] font-bold tracking-tight">42k scans / week</span>
        </motion.div>
      </motion.div>
    </div>
  );
}

// Data-driven constants (icons/style only; text via t())
const BIG_FEATURES = [
  { icon: Radio,  key: "1", color: "destructive", img: "https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { icon: MapPin, key: "2", color: "primary",     img: "https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { icon: Route,  key: "3", color: "secondary",   img: "https://images.unsplash.com/photo-1625690180114-5530b1304127?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { icon: Zap,    key: "4", color: "accent",      img: "https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
];
const CATEGORIES = [
  { icon: Car, key: "vehicle", tint: "primary", img: "https://images.unsplash.com/photo-1625690180114-5530b1304127?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: Baby, key: "child", tint: "accent", img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: Users, key: "family", tint: "secondary", img: "https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: HeartHandshake, key: "senior", tint: "destructive", img: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: Dog, key: "pet", tint: "accent", img: "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: Package, key: "lostfound", tint: "secondary", img: "https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: Building2, key: "corporate", tint: "primary", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { icon: Ambulance, key: "emergency", tint: "destructive", img: "https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
];
const MODULE_ICONS = [QrCode, Siren, Users, Car, Search, MessageCircle, ShieldAlert, Lock];
const TESTI_IDS = [1, 2, 3, 4];
const FAQ_IDS = [1, 2, 3, 4, 5];
const CERTS = ["ISO 27001", "GDPR", "SOC 2 Type II", "PCI DSS", "Make in India", "MSME"];
const PORTAL_LIST = ["Volunteer", "Police", "Hospital", "Ambulance", "Corporate", "NGO", "Fleet", "Support", "Community", "Admin", "Developer", "Auditor"];
const STORE_ANDROID = process.env.REACT_APP_STORE_ANDROID_URL || "";
const STORE_IOS = process.env.REACT_APP_STORE_IOS_URL || "";

// ---------------- Component ----------------
export default function Landing() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useLang();
  const nav = useNavigate();
  const goCta = () => nav(user ? "/app" : "/register");
  const [testi, setTesti] = useState(0);
  const [liveTesti, setLiveTesti] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 400], [0, -60]);
  const heroFade = useTransform(scrollY, [0, 300], [1, 0.5]);

  useEffect(() => { document.title = "Guardian360 · One Scan. Complete Protection."; }, []);
  useEffect(() => {
    const id = setInterval(() => setTesti((v) => (v + 1) % Math.max(1, (liveTesti.length || TESTI_IDS.length))), 5000);
    return () => clearInterval(id);
  }, [liveTesti.length]);

  // Load moderated testimonials from backend; fall back to i18n set if none.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials?limit=12`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setLiveTesti(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, []);

  const testiSource = liveTesti.length ? liveTesti.map((r) => ({
    id: r.id, name: r.name, city: r.city || "", quote: r.quote, stars: r.stars || 5,
  })) : TESTI_IDS.map((id) => ({
    id, name: t(`testi_${id}_name`), city: t(`testi_${id}_city`),
    quote: t(`testi_${id}_quote`), stars: 5,
  }));
  const active = testiSource[testi % testiSource.length] || testiSource[0];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* ================= NAV ================= */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] grid place-items-center">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">Guardian360</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">One Scan · Complete Protection</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-foreground/70 hover:text-foreground transition-colors">{t("nav_features")}</a>
            <a href="#how" className="text-foreground/70 hover:text-foreground transition-colors">{t("nav_how")}</a>
            <a href="#pricing" className="text-foreground/70 hover:text-foreground transition-colors">{t("nav_pricing")}</a>
            <a href="#portals" className="text-foreground/70 hover:text-foreground transition-colors">{t("nav_portals")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LangToggle />
            <Button data-testid="theme-toggle-landing" variant="ghost" size="icon" onClick={toggle}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            {user ? (
              <Button data-testid="dashboard-cta" onClick={() => nav("/app")}>{t("nav_dashboard")}</Button>
            ) : (
              <>
                <Button data-testid="login-cta" variant="ghost" onClick={() => nav("/login")}>{t("nav_signin")}</Button>
                <Button data-testid="signup-cta" onClick={() => nav("/register")} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
                  {t("nav_signup")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Live status ticker */}
      <div className="border-b border-border/50 bg-[hsl(var(--secondary))]/8">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center gap-3 overflow-hidden">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--secondary))] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--secondary))]" />
            </span>
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--secondary))]">LIVE</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="marquee-track flex gap-12 whitespace-nowrap text-xs text-muted-foreground">
              {[...Array(2)].map((_, k) => (
                <div key={k} className="flex gap-12 items-center">
                  <span><b className="text-foreground">1,247</b> SOS resolved this week</span>
                  <span>·</span>
                  <span><b className="text-foreground">42,318</b> QR scans processed</span>
                  <span>·</span>
                  <span><b className="text-foreground">312</b> families reunited via Lost & Found</span>
                  <span>·</span>
                  <span><b className="text-foreground">18</b> cities live</span>
                  <span>·</span>
                  <span><b className="text-foreground">99.98%</b> uptime</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden">
        <div className="aurora bg-[hsl(var(--primary))]" style={{ top: "-140px", left: "-160px" }} />
        <div className="aurora bg-[hsl(var(--secondary))]" style={{ top: "60px", right: "-180px" }} />
        <div className="aurora bg-[hsl(var(--accent))] opacity-40" style={{ bottom: "-260px", left: "35%" }} />

        {/* Animated dotted grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
          aria-hidden
        />

        <motion.div style={{ y: heroY, opacity: heroFade }} className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-24 relative z-10">
          <motion.div variants={stagger} initial="hidden" animate="show" className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.div variants={fadeUp}>
                <Badge variant="outline" className="mb-6 rounded-full border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 px-3 py-1">
                  <motion.span
                    animate={{ rotate: [0, 20, -20, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity }}
                    className="inline-flex mr-1.5"
                  >
                    <Sparkles className="w-3 h-3" />
                  </motion.span>
                  {t("hero_badge")}
                </Badge>
              </motion.div>

              <motion.h1 variants={fadeUp} className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
                <span className="block">{t("hero_line1")}</span>
                <span className="block">{t("hero_line2")}</span>
                <motion.span
                  className="block bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--accent))] to-[hsl(var(--secondary))] bg-clip-text text-transparent"
                  style={{ backgroundSize: "220% auto" }}
                  animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                >
                  {t("hero_line3")}
                </motion.span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                {t("hero_sub")}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    data-testid="hero-cta-start"
                    size="lg"
                    onClick={goCta}
                    className="relative overflow-hidden bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full px-7 h-12 shadow-lg shadow-[hsl(var(--primary))]/25 group"
                  >
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                    <span className="relative">{user ? t("nav_dashboard") : t("cta_protect")}</span>
                    <ArrowRight className="w-4 h-4 ml-2 relative" />
                  </Button>
                </motion.div>
                <Button data-testid="hero-cta-demo" size="lg" variant="outline" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full px-7 h-12">
                  <Play className="w-3.5 h-3.5 mr-2 fill-current" /> {t("cta_how")}
                </Button>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-8 mt-10 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[hsl(var(--secondary))]" /> {t("ok_no_cc")}</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[hsl(var(--secondary))]" /> {t("ok_gdpr")}</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[hsl(var(--secondary))]" /> {t("ok_offline")}</div>
              </motion.div>

              {/* Trusted-by strip */}
              <motion.div variants={fadeUp} className="mt-12 pt-6 border-t border-border/50">
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-semibold">Trusted across India</div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  {["Mumbai Police", "AIIMS Delhi", "IIT Bombay", "Bengaluru Traffic", "Hyderabad Metro"].map((org, i) => (
                    <motion.div
                      key={org}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 0.65, y: 0 }}
                      transition={{ delay: 1.1 + i * 0.08 }}
                      whileHover={{ opacity: 1, y: -2 }}
                      className="text-xs font-semibold tracking-tight text-foreground/80"
                    >
                      {org}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="lg:col-span-5 relative">
              <HeroCollage t={t} />
            </motion.div>
          </motion.div>

          {/* Scroll cue */}
          <motion.a href="#features" animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="mt-14 hidden md:flex items-center justify-center gap-2 text-xs text-muted-foreground">
            Scroll to explore <ChevronDown className="w-3.5 h-3.5" />
          </motion.a>
        </motion.div>
      </section>

      {/* ================= BIG FEATURES ================= */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20 relative">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] mb-3 font-semibold">{t("cap_kicker")}</div>
          <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">{t("cap_title")}</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{t("cap_sub")}</p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-4">
          {BIG_FEATURES.map((f, i) => (
            <motion.div
              key={f.tag}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              whileHover={{ y: -6 }}
              className="group relative rounded-3xl overflow-hidden border border-border/70 bg-card p-8 h-72 card-shadow"
            >
              <div className="absolute inset-0 opacity-25 group-hover:opacity-40 transition-opacity">
                <img src={f.img} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-card/30" />
              </div>
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-2xl bg-[hsl(var(--${f.color}))]/12 grid place-items-center`}>
                    <f.icon className={`w-6 h-6 text-[hsl(var(--${f.color}))]`} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">{t(`cap_${f.key}_tag`)}</div>
                </div>
                <div className="mt-auto">
                  <div className="font-display font-black text-3xl tracking-tighter mb-2">{t(`cap_${f.key}_title`)}</div>
                  <div className="text-muted-foreground max-w-sm">{t(`cap_${f.key}_desc`)}</div>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity">
                    {t("cap_explore")} <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= LIVE STATS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-border/50">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: 12000, s: "+", label: t("stat_families"), color: "primary" },
            { n: 42318, s: "",  label: t("stat_scans"),    color: "secondary" },
            { n: 1247,  s: "",  label: t("stat_sos"),      color: "destructive" },
            { n: 99,    s: "%", label: t("stat_sla"),      color: "accent" },
          ].map((x, i) => (
            <motion.div
              key={x.label}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-border/70 bg-card p-6"
            >
              <div className={`w-8 h-8 rounded-lg bg-[hsl(var(--${x.color}))]/12 grid place-items-center mb-3`}>
                <Star className={`w-3.5 h-3.5 text-[hsl(var(--${x.color}))]`} />
              </div>
              <div className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                <CountUp to={x.n} suffix={x.s} />
              </div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{x.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= MOMENTS GALLERY (bento) ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--secondary))] mb-3 font-semibold">Real stories · Real people</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Guardian360 in the wild.</h2>
          </div>
          <p className="text-muted-foreground max-w-md">A day in the life of our safety network — schools, streets, highways, hospitals and homes.</p>
        </motion.div>

        <div className="grid grid-cols-6 auto-rows-[110px] sm:auto-rows-[140px] lg:auto-rows-[160px] gap-3">
          {[
            { img: "https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80", label: "QR scan → live location", tint: "primary", span: "col-span-4 row-span-2" },
            { img: "https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", label: "Family safely reunited", tint: "secondary", span: "col-span-2 row-span-2" },
            { img: "https://images.unsplash.com/photo-1625690180114-5530b1304127?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", label: "Highway SOS routing", tint: "accent", span: "col-span-2 row-span-2" },
            { img: "https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80", label: "3.2s responder dispatch", tint: "destructive", span: "col-span-4 row-span-2" },
            { img: "https://images.unsplash.com/photo-1625690180114-5530b1304127?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&sat=-100", label: "18 cities, one network", tint: "primary", span: "col-span-3 row-span-1" },
            { img: "https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&sat=-100", label: "Verified NGO responders", tint: "secondary", span: "col-span-3 row-span-1" },
          ].map((tile, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.07, duration: 0.5 }}
              whileHover={{ y: -4 }}
              className={`relative overflow-hidden rounded-2xl border border-border/60 group ${tile.span}`}
              data-testid={`moment-tile-${i}`}
            >
              <motion.img
                src={tile.img}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.05 }}
                whileHover={{ scale: 1.12 }}
                transition={{ duration: 0.8 }}
              />
              <div className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent`} />
              <div className={`absolute top-3 left-3 w-6 h-6 rounded-full bg-[hsl(var(--${tile.tint}))] grid place-items-center shadow-lg`}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-white font-semibold text-sm sm:text-base tracking-tight drop-shadow-md">{tile.label}</div>
                <div className="mt-1 h-0.5 w-8 bg-[hsl(var(--${tile.tint}))] group-hover:w-16 transition-all duration-500" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= TOP CATEGORIES ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))] mb-3 font-semibold">{t("cat_kicker")}</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">{t("cat_title")}</h2>
          </div>
          <p className="text-muted-foreground max-w-md">{t("cat_sub")}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CATEGORIES.map((c, i) => (
            <motion.button
              key={c.key}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              onClick={goCta}
              className="group relative rounded-2xl border border-border/70 bg-card overflow-hidden text-left hover:border-[hsl(var(--primary))]/60 hover:-translate-y-1 transition-transform"
              data-testid={`category-${c.key}`}
            >
              <div className="relative h-28 overflow-hidden">
                <motion.img
                  src={c.img}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.12 }}
                  transition={{ duration: 0.7 }}
                />
                <div className={`absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent`} />
                <div className={`absolute top-3 left-3 w-9 h-9 rounded-xl bg-background/95 backdrop-blur border border-border/60 grid place-items-center group-hover:bg-[hsl(var(--${c.tint}))] transition-colors shadow-md`}>
                  <c.icon className={`w-4 h-4 text-[hsl(var(--${c.tint}))] group-hover:text-white transition-colors`} />
                </div>
              </div>
              <div className="p-4">
                <div className="font-display font-bold tracking-tight">{t(`cat_${c.key}`)}</div>
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">{t("cat_get_qr")} <ArrowUpRight className="w-3 h-3" /></div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24 border-t border-border/50">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))] mb-3 font-semibold">{t("steps_kicker")}</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-6">{t("steps_title")}</h2>
            <p className="text-muted-foreground mb-8">{t("steps_sub")}</p>
            <Button data-testid="how-cta" onClick={goCta} className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              {t("steps_cta")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {/* Photo collage below CTA */}
            <div className="mt-10 grid grid-cols-2 gap-3">
              <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-border/60 shadow-lg"
              >
                <img src="https://images.unsplash.com/photo-1604357209793-fca5dca89f97?crop=entropy&cs=srgb&fm=jpg&w=500&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--primary))]/80 to-transparent" />
                <div className="absolute bottom-3 left-3 text-white text-xs font-bold uppercase tracking-wider">01 · Scan</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                whileHover={{ y: -4 }}
                className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-border/60 shadow-lg mt-6"
              >
                <img src="https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&w=500&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--secondary))]/80 to-transparent" />
                <div className="absolute bottom-3 left-3 text-white text-xs font-bold uppercase tracking-wider">02 · Alert</div>
              </motion.div>
            </div>
          </div>
        <div className="lg:col-span-8 space-y-4">
          {[1, 2, 3].map((n, i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group rounded-2xl border border-border/70 bg-card p-6 flex gap-6 items-start hover:border-[hsl(var(--primary))]/50 transition-colors"
            >
              <div className="text-5xl font-display font-black text-[hsl(var(--primary))]/20 tracking-tighter group-hover:text-[hsl(var(--primary))] transition-colors">0{n}</div>
              <div>
                <div className="font-display font-bold text-xl mb-1.5">{t(`step_${n}_t`)}</div>
                <div className="text-muted-foreground">{t(`step_${n}_d`)}</div>
              </div>
            </motion.div>
          ))}
        </div>
        </div>
      </section>

      {/* ================= MODULE GRID ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] mb-3 font-semibold">{t("fs_kicker")}</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter max-w-3xl mb-4">{t("fs_title")}</h2>
        <p className="text-muted-foreground max-w-md mb-10">{t("fs_sub")}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {MODULE_ICONS.map((Icon, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="group rounded-2xl border border-border/60 bg-card p-6 hover:border-[hsl(var(--primary))]/60 hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 grid place-items-center mb-4 group-hover:bg-[hsl(var(--primary))] transition-colors">
                <Icon className="w-5 h-5 text-[hsl(var(--primary))] group-hover:text-white transition-colors" />
              </div>
              <div className="font-display font-bold text-lg mb-1.5 tracking-tight">{t(`mod_${i + 1}_t`)}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{t(`mod_${i + 1}_d`)}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= APP DOWNLOAD ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--secondary))] mb-3 font-semibold">{t("app_kicker")}</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-4">{t("app_title")}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg">{t("app_sub")}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              <StoreLink href={STORE_ANDROID} kicker={t("app_get_google")} label={t("app_google")} coming={t("app_coming")} testId="store-android" />
              <StoreLink href={STORE_IOS}     kicker={t("app_get_apple")}  label={t("app_apple")}  coming={t("app_coming")} testId="store-ios" />
            </div>
            <div className="flex items-center gap-6">
              <div>
                <div className="font-display font-black text-3xl tracking-tighter">4.8 <Star className="inline w-4 h-4 fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" /></div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">{t("app_rating")}</div>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <div className="font-display font-black text-3xl tracking-tighter"><CountUp to={2} />M+</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">{t("app_downloads")}</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity }} className="mx-auto max-w-sm rounded-[42px] border-8 border-foreground bg-foreground p-2 shadow-2xl">
              <div className="rounded-[32px] overflow-hidden bg-[hsl(var(--background))] aspect-[9/19] relative">
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] grid place-items-center"><Shield className="w-4 h-4 text-white" /></div>
                    <div className="text-[10px] uppercase tracking-wider">4:20</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("app_console")}</div>
                    <div className="font-display font-black text-2xl tracking-tighter">{t("app_greet")}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-2xl bg-[hsl(var(--primary))] p-4"><Siren className="w-5 h-5 text-white mb-2" /><div className="text-white font-semibold text-sm">SOS</div></div>
                    <div className="rounded-2xl border border-border p-4"><QrCode className="w-5 h-5 mb-2" /><div className="font-semibold text-sm">{t("side_qr")}</div></div>
                    <div className="rounded-2xl border border-border p-4"><Users className="w-5 h-5 mb-2" /><div className="font-semibold text-sm">{t("side_family")}</div></div>
                    <div className="rounded-2xl border border-border p-4"><MessageCircle className="w-5 h-5 mb-2" /><div className="font-semibold text-sm">AI</div></div>
                  </div>
                  <div className="rounded-2xl bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/30 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--destructive))]">{t("app_live_sos")}</div>
                    <div className="text-sm font-semibold">{t("app_responders")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= PORTALS ================= */}
      <section id="portals" className="max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--secondary))] mb-3 font-semibold">{t("portals_kicker")}</div>
        <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-10 max-w-3xl">{t("portals_title")}</h2>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
          {PORTAL_LIST.map((p) => (
            <motion.div
              key={p}
              whileHover={{ y: -4 }}
              className="rounded-xl border border-border/60 bg-card px-4 py-5 text-center hover:border-[hsl(var(--primary))]/60 transition-colors"
            >
              <div className="font-semibold text-sm">{p}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{t("portal_role")}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))] mb-3 font-semibold">{t("testi_kicker")}</div>
        <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-14 max-w-3xl">{t("testi_title")}</h2>
        <div className="grid lg:grid-cols-12 gap-6">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-8 rounded-3xl border border-border/70 bg-card p-10 relative overflow-hidden"
          >
            <Quote className="absolute top-6 right-6 w-24 h-24 text-[hsl(var(--primary))]/8" />
            <div className="flex gap-1 mb-6">
              {Array(active.stars).fill(0).map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" />
              ))}
            </div>
            <div className="font-display text-2xl lg:text-3xl tracking-tight leading-snug mb-6">&ldquo;{active.quote}&rdquo;</div>
            <div className="flex items-center gap-3">
              <img
                src={`https://i.pravatar.cc/120?u=${encodeURIComponent(active.name || active.id)}`}
                alt={active.name}
                loading="lazy"
                className="w-12 h-12 rounded-full object-cover border-2 border-background shadow-md"
              />
              <div>
                <div className="font-semibold">{active.name}</div>
                <div className="text-sm text-muted-foreground">{active.city}</div>
              </div>
            </div>
          </motion.div>
          <div className="lg:col-span-4 space-y-2">
            {testiSource.map((r, i) => (
              <button
                key={r.id}
                onClick={() => setTesti(i)}
                data-testid={`testi-select-${i}`}
                className={`w-full text-left rounded-2xl border p-3 transition-colors flex items-center gap-3 ${testi === i ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border/70 bg-card hover:border-border"}`}
              >
                <img
                  src={`https://i.pravatar.cc/80?u=${encodeURIComponent(r.name || r.id)}`}
                  alt=""
                  loading="lazy"
                  className="w-10 h-10 rounded-full object-cover shrink-0"
                />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.city}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CERTIFICATIONS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-14 border-t border-border/50">
        <div className="flex items-center justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1 font-semibold">{t("cert_kicker")}</div>
            <div className="font-display font-black text-2xl tracking-tighter">{t("cert_title")}</div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Award className="w-4 h-4 text-[hsl(var(--secondary))]" /> {t("cert_audit")}
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {CERTS.map((c) => (
            <div key={c} className="rounded-xl border border-border/60 bg-card px-4 py-4 text-center">
              <Fingerprint className="w-4 h-4 mx-auto mb-2 text-muted-foreground" />
              <div className="font-semibold text-xs uppercase tracking-wider">{c}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 border-t border-border/50">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] mb-3 font-semibold">{t("pricing_kicker")}</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-3">{t("pricing_title")}</h2>
          <p className="text-muted-foreground">{t("pricing_sub")}</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          {[
            { id: "free", price: "₹0",   highlight: false, feats: 4 },
            { id: "plus", price: "₹399", highlight: true,  feats: 5 },
            { id: "corp", price: t("tier_corp_price"), highlight: false, feats: 4 },
          ].map((tt, i) => (
            <motion.div
              key={tt.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`rounded-2xl p-8 border transition-transform hover:-translate-y-1 ${tt.highlight ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.03] card-shadow" : "border-border/70 bg-card"}`}
            >
              {tt.highlight && <Badge className="mb-4 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]">{t("tier_plus_popular")}</Badge>}
              <div className="font-display font-bold text-xl mb-2">{t(`tier_${tt.id}`)}</div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-display font-black text-5xl tracking-tighter">{tt.price}</span>
                <span className="text-muted-foreground text-sm">{t(`tier_${tt.id}_tag`)}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {Array.from({ length: tt.feats }).map((_, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-[hsl(var(--secondary))] mt-0.5 shrink-0" /> {t(`tier_${tt.id}_${k + 1}`)}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`pricing-cta-${tt.id}`}
                onClick={goCta}
                className={`w-full rounded-full ${tt.highlight ? "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" : "bg-foreground text-background hover:bg-foreground/90"}`}
              >
                {t(`tier_${tt.id}_cta`)}
              </Button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="max-w-4xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))] mb-3 font-semibold">{t("faq_kicker")}</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">{t("faq_title")}</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQ_IDS.map((id) => (
            <AccordionItem key={id} value={`faq-${id}`} className="rounded-2xl border border-border/70 bg-card px-6 data-[state=open]:border-[hsl(var(--primary))]/50">
              <AccordionTrigger data-testid={`faq-${id}`} className="text-left font-display font-semibold text-lg hover:no-underline">{t(`faq_${id}_q`)}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-6 leading-relaxed">{t(`faq_${id}_a`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ================= CTA ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative overflow-hidden rounded-3xl bg-foreground text-background p-12 lg:p-20">
          {/* Background photo */}
          <div className="absolute inset-0">
            <motion.img
              src="https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
              alt=""
              loading="lazy"
              className="w-full h-full object-cover opacity-40"
              initial={{ scale: 1.1 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 2, ease: "easeOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/80 to-foreground/30" />
          </div>
          <div className="aurora bg-[hsl(var(--primary))] opacity-70" style={{ bottom: "-200px", right: "-100px" }} />
          <div className="aurora bg-[hsl(var(--accent))] opacity-40" style={{ top: "-160px", left: "-120px" }} />
          <div className="relative z-10 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-background/50 mb-4">{t("bigcta_kicker")}</div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter mb-6">{t("bigcta_title")}</h2>
            <p className="text-background/70 text-lg mb-8">{t("bigcta_sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="footer-cta" size="lg" onClick={goCta} className="bg-white text-black hover:bg-white/90 rounded-full px-7 h-12">
                {t("bigcta_btn")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white/10 hover:text-white rounded-full px-7 h-12">
                <Phone className="w-4 h-4 mr-2" /> {t("bigcta_sales")}
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-border/60 py-14">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))] grid place-items-center"><Shield className="w-4 h-4 text-white" /></div>
              <div className="font-display font-black text-lg tracking-tight">Guardian360</div>
            </div>
            <p className="text-sm text-muted-foreground max-w-sm">{t("footer_desc")}</p>
          </div>
          {[
            { h: t("footer_h_product"),  l: [t("footer_l_features"), t("footer_l_how"), t("footer_l_pricing"), t("footer_l_mobile")] },
            { h: t("footer_h_portals"),  l: [t("footer_l_volunteer"), t("footer_l_police"), t("footer_l_hospital"), t("footer_l_corporate")] },
            { h: t("footer_h_company"),  l: [t("footer_l_about"), t("footer_l_blog"), t("footer_l_careers"), t("footer_l_contact")] },
            { h: t("footer_h_legal"),    l: [t("footer_l_privacy"), t("footer_l_terms"), t("footer_l_security"), t("footer_l_gdpr")] },
          ].map((col) => (
            <div key={col.h} className="lg:col-span-2 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{col.h}</div>
              <ul className="space-y-2">
                {col.l.map((x) => <li key={x}><a href="#" className="text-foreground/70 hover:text-foreground transition-colors">{x}</a></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-border/50 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Guardian360 · {t("footer_rights")}</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-foreground">Twitter</a>
            <a href="#" className="hover:text-foreground">LinkedIn</a>
            <a href="#" className="hover:text-foreground">Instagram</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
